/**
 * React Query 统一缓存配置
 *
 * ⚠️ 开发调试优先策略：
 * - 当前设置：ENABLE_CACHE = false（每次都请求 API）
 * - 目的：在开发阶段暴露所有问题，避免缓存掩盖 bug
 *
 * 生产环境优化：
 * - 如需启用缓存：将 ENABLE_CACHE 改为 true
 * - 或者使用：export const ENABLE_CACHE = import.meta.env.PROD;
 *
 * staleTime: 数据被认为是"新鲜"的时间
 *   - 在此时间内使用缓存，不请求 API
 *   - 超过此时间，下次挂载时会请求 API
 *
 * gcTime: 数据在内存中保留的时间
 *   - 超过此时间，缓存被删除（释放内存）
 *   - gcTime 应该 >= staleTime
 */

/**
 * 全局缓存开关
 *
 * false = 禁用缓存（开发调试，每次都请求 API）
 * true  = 启用缓存（生产优化，减少 API 调用）
 *
 * 生产环境可改为：
 * export const ENABLE_CACHE = import.meta.env.PROD;
 */
export const ENABLE_CACHE = false;

/** 缓存时间配置（当 ENABLE_CACHE = true 时使用） */
const CACHE_TIMES = {
  /** 30s - 实时数据（股价、市场数据） */
  REALTIME: {
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  },

  /** 2m - 频繁更新的数据（memo列表、活动流） */
  FREQUENT: {
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },

  /** 5m - 半静态数据（ticker、用户设置） */
  MODERATE: {
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  },

  /** 30m - 静态数据（节假日、枚举值） */
  STATIC: {
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  },
} as const;

/** 禁用缓存的配置（调试模式） */
const NO_CACHE = {
  staleTime: 0,
  gcTime: 0,
};

/**
 * 导出统一缓存配置
 *
 * 使用示例：
 * ```ts
 * import { CACHE_CONFIG } from "@/lib/react-query";
 *
 * useQuery({
 *   queryKey: ["tickers"],
 *   queryFn: () => fetchTickers(),
 *   ...CACHE_CONFIG.MODERATE, // ticker 使用中等缓存
 * });
 * ```
 */
export const CACHE_CONFIG = ENABLE_CACHE
  ? {
      REALTIME: CACHE_TIMES.REALTIME,
      FREQUENT: CACHE_TIMES.FREQUENT,
      MODERATE: CACHE_TIMES.MODERATE,
      STATIC: CACHE_TIMES.STATIC,
    }
  : {
      REALTIME: NO_CACHE,
      FREQUENT: NO_CACHE,
      MODERATE: NO_CACHE,
      STATIC: NO_CACHE,
    };

export type CacheConfig = typeof CACHE_CONFIG;
